import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import { Plus, CalendarDays, Clock, X, Edit2, Trash2, CheckCircle, MapPin, Upload, User, RefreshCw, Users, AlertTriangle, FolderPlus } from 'lucide-react';

const emptyForm = {
  // Empresa
  usarClienteRegistrado: false,
  clienteId: '', contactoNombre: '', contactoEmail: '', contactoTelefono: '',
  empresaLugar: '',
  // Ubicacion
  direccion: '', linkMaps: '',
  // Fecha/hora
  fecha: '', hora: '',
  tema: '', documentosNecesarios: '', notas: '',
  estado: 'programada',
  // Invitado
  invitadoId: '',
  // Adjunto
  adjuntoNombre: '', adjuntoData: '', adjuntoTipo: '',
};

const badgeEstado = (e) => {
  const map = { programada:'badge-blue', completada:'badge-green', cancelada:'badge-red', reagendada:'badge-orange', declinada:'badge-gray' };
  return <span className={`badge ${map[e]||'badge-gray'}`}>{e}</span>;
};

// ── MAP SELECTOR (OpenStreetMap + Leaflet, sin API key) ───────────────────
// Mapa interactivo real: se puede hacer clic o arrastrar el pin para marcar el punto exacto,
// igual que en Google Maps. La busqueda por nombre sigue usando Nominatim (gratis) pero ya no
// es la unica forma de ubicar el punto — si no encuentra el lugar, el usuario lo marca a mano.
const GUAYAQUIL_DEFAULT = { lat: -2.170998, lng: -79.922359 };

function loadLeaflet() {
  return new Promise((resolve) => {
    if (window.L) { resolve(window.L); return; }
    if (!document.getElementById('leaflet-css')) {
      const css = document.createElement('link');
      css.id = 'leaflet-css';
      css.rel = 'stylesheet';
      css.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
      document.head.appendChild(css);
    }
    const existing = document.getElementById('leaflet-js');
    if (existing) { existing.addEventListener('load', () => resolve(window.L)); return; }
    const s = document.createElement('script');
    s.id = 'leaflet-js';
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';
    s.onload = () => resolve(window.L);
    document.body.appendChild(s);
  });
}

function MapSelector({ direccion, linkMaps, latitud, longitud, onSelect }) {
  const [query, setQuery] = React.useState(direccion || '');
  const [results, setResults] = React.useState([]);
  const [searching, setSearching] = React.useState(false);
  const initial = (latitud && longitud) ? { lat: parseFloat(latitud), lng: parseFloat(longitud) } : null;
  const [selected, setSelected] = React.useState(initial);

  const mapElRef = React.useRef(null);
  const mapRef = React.useRef(null);
  const markerRef = React.useRef(null);
  const onSelectRef = React.useRef(onSelect);
  onSelectRef.current = onSelect;

  const reverseGeocode = async (lat, lng) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
        { headers: { 'Accept-Language': 'es' } }
      );
      const data = await res.json();
      return data.display_name || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    } catch { return `${lat.toFixed(6)}, ${lng.toFixed(6)}`; }
  };

  const applyPoint = async (lat, lng, dirHint) => {
    const dirFull = dirHint || await reverseGeocode(lat, lng);
    const dirCorta = dirFull.split(',').slice(0,3).join(',');
    const link = `https://www.google.com/maps?q=${lat},${lng}`;
    setSelected({ lat, lng });
    setQuery(dirCorta);
    onSelectRef.current(dirCorta, link, lat, lng);
  };

  const placeMarker = (L, map, lat, lng, draggable=true) => {
    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    } else {
      markerRef.current = L.marker([lat, lng], { draggable }).addTo(map);
      markerRef.current.on('dragend', () => {
        const pos = markerRef.current.getLatLng();
        applyPoint(pos.lat, pos.lng);
      });
    }
  };

  // Inicializa el mapa una sola vez (se destruye al desmontar, ej. al cerrar el modal)
  React.useEffect(() => {
    let cancelled = false;
    loadLeaflet().then((L) => {
      if (cancelled || !mapElRef.current || mapRef.current) return;
      const start = initial || GUAYAQUIL_DEFAULT;
      const map = L.map(mapElRef.current).setView([start.lat, start.lng], initial ? 16 : 13);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap', maxZoom: 19,
      }).addTo(map);
      mapRef.current = map;
      if (initial) placeMarker(L, map, initial.lat, initial.lng);
      map.on('click', (e) => {
        placeMarker(L, map, e.latlng.lat, e.latlng.lng);
        applyPoint(e.latlng.lat, e.latlng.lng);
      });
      setTimeout(() => map.invalidateSize(), 200);
    });
    return () => {
      cancelled = true;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; markerRef.current = null; }
    };
  }, []);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=6&addressdetails=1&countrycodes=ec`,
        { headers: { 'Accept-Language': 'es' } }
      );
      const data = await res.json();
      setResults(data);
      if (data.length === 0) {
        toast.error('No se encontró nada con ese nombre. Puedes hacer clic directo en el mapa para marcar el punto exacto, o arrastrar el pin.');
      }
    } catch (e) { console.error(e); }
    finally { setSearching(false); }
  };

  const handleSelect = (place) => {
    const lat = parseFloat(place.lat);
    const lng = parseFloat(place.lon);
    setResults([]);
    if (mapRef.current && window.L) {
      mapRef.current.setView([lat, lng], 16);
      placeMarker(window.L, mapRef.current, lat, lng);
    }
    applyPoint(lat, lng, place.display_name);
  };

  return (
    <div>
      <div style={{ display:'flex', gap:8, marginBottom:8 }}>
        <input className="form-input" style={{ flex:1 }} placeholder="Buscar lugar, empresa, direccion..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleSearch())}
        />
        <button type="button" className="btn btn-secondary" onClick={handleSearch} disabled={searching} style={{ flexShrink:0 }}>
          {searching ? '...' : 'Buscar'}
        </button>
      </div>

      {/* Search results */}
      {results.length > 0 && (
        <div style={{ border:'1px solid #e5e5e5', borderRadius:8, overflow:'hidden', marginBottom:8, boxShadow:'0 2px 8px rgba(0,0,0,0.1)' }}>
          {results.map((r, i) => (
            <div key={i} onClick={() => handleSelect(r)}
              style={{ padding:'8px 12px', cursor:'pointer', borderBottom:'1px solid #f0f0f0', fontSize:13 }}
              onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
              onMouseLeave={e => e.currentTarget.style.background = 'white'}>
              <div style={{ fontWeight:500 }}>{r.display_name.split(',').slice(0,2).join(',')}</div>
              <div style={{ fontSize:11, color:'#888' }}>{r.display_name.split(',').slice(2,5).join(',')}</div>
            </div>
          ))}
        </div>
      )}

      {/* Mapa interactivo: clic o arrastrar el pin para marcar el punto exacto */}
      <div ref={mapElRef} style={{ width:'100%', height:260, borderRadius:8, border:'1px solid #e5e5e5', marginBottom:8 }}/>

      {selected && (
        <div style={{ padding:'6px 12px', background:'#f0f9ff', fontSize:11, color:'#0369a1', display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
          <span>Ubicacion seleccionada — clic o arrastra el pin para ajustar</span>
          <a href={`https://www.google.com/maps?q=${selected.lat},${selected.lng}`} target="_blank" rel="noopener noreferrer"
            style={{ color:'#7B0D0D', fontWeight:600, fontSize:12 }}>
            Abrir en Google Maps
          </a>
        </div>
      )}

      {/* Show existing link if already saved */}
      {!selected && linkMaps && (
        <div style={{ padding:'6px 12px', background:'#f0fdf4', borderRadius:8, fontSize:12, marginBottom:8 }}>
          <span style={{ color:'#888' }}>Link guardado: </span>
          <a href={linkMaps} target="_blank" rel="noopener noreferrer" style={{ color:'#7B0D0D', fontWeight:600 }}>
            Ver en mapa
          </a>
        </div>
      )}

      <span style={{ fontSize:11, color:'#888' }}>
        Busca el lugar, o haz clic directo en el mapa (o arrastra el pin) para marcar el punto exacto.
      </span>
    </div>
  );
}

export default function CitasPage() {
  const { user } = useAuth();
  const isGerencia = ['admin','master','gerente_financiera','gerente_proyectos'].includes(user?.rol);
  const navigate = useNavigate();
  const location = useLocation();
  const [citas, setCitas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [filtro, setFiltro] = useState('proximas');
  const [filtroUsuario, setFiltroUsuario] = useState('');
  const [todosUsuarios, setTodosUsuarios] = useState([]);

  // For form
  const [clientes, setClientes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [checkingDisp, setCheckingDisp] = useState(false);
  const [dispStatus, setDispStatus] = useState(null); // null | 'disponible' | 'ocupado'
  const [clienteSel, setClienteSel] = useState(null);
  const adjRef = useRef();
  const [modalCompletar, setModalCompletar] = useState(null);
  const [pasoCompletar, setPasoCompletar] = useState('preguntar'); // 'preguntar' | 'exito' | 'fallo'
  const [necesidadInput, setNecesidadInput] = useState('');
  const [clienteNuevoForm, setClienteNuevoForm] = useState({ empresa:'', contactoNombre:'', contactoEmail:'', contactoTelefono:'' });
  const [savingCompletar, setSavingCompletar] = useState(false);

  const load = async () => {
    try { const r = await api.get('/appointments'); setCitas(r.data); }
    catch { toast.error('Error al cargar citas'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Llega desde un clic en la Agenda compartida sobre esta cita
  useEffect(() => {
    if (location.state?.openCitaId && citas.length > 0) {
      const c = citas.find(x => x.id === location.state.openCitaId);
      if (c) openEditar(c);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, citas]);

  useEffect(() => {
    api.get('/clients').then(r => setClientes(r.data)).catch(() => {});
    api.get('/users/all').then(r => {
      const lista = Array.isArray(r.data) ? r.data : [];
      setUsuarios(lista.filter(u => u.id !== user?.id && u.activo !== false));
      if (isGerencia) setTodosUsuarios(lista.filter(u => u.activo !== false));
    }).catch(() => {});
  }, [user, isGerencia]);

  const fld = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));
  const fldCheck = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.checked }));

  // When cliente is selected, auto-fill empresa and contacto (contacto único del cliente)
  const handleClienteChange = (clienteId) => {
    const c = clientes.find(cl => cl.id === clienteId);
    setClienteSel(c || null);
    setForm(prev => ({
      ...prev,
      clienteId,
      empresaLugar: c?.empresa || prev.empresaLugar,
      contactoNombre: c?.contactoNombre || '',
      contactoEmail: c?.contactoEmail || '',
      contactoTelefono: c?.contactoTelefono || '',
    }));
  };

  // Check availability when invitado + fecha + hora change
  useEffect(() => {
    if (!form.invitadoId || !form.fecha || !form.hora) { setDispStatus(null); return; }
    setCheckingDisp(true);
    const timeout = setTimeout(async () => {
      try {
        const r = await api.get('/appointments/disponibilidad', {
          params: { userId: form.invitadoId, fecha: form.fecha, hora: form.hora, excludeId: modal === 'editar' ? selected?.id : undefined }
        });
        setDispStatus(r.data.disponible ? 'disponible' : 'ocupado');
      } catch { setDispStatus(null); }
      finally { setCheckingDisp(false); }
    }, 600);
    return () => clearTimeout(timeout);
  }, [form.invitadoId, form.fecha, form.hora]);

  // Handle adjunto upload
  const handleAdjunto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error('Adjunto max. 3MB'); return; }
    const reader = new FileReader();
    reader.onload = ev => setForm(prev => ({
      ...prev,
      adjuntoNombre: file.name,
      adjuntoData: ev.target.result,
      adjuntoTipo: file.type,
    }));
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const openCrear = () => {
    setForm({ ...emptyForm, fecha: new Date().toISOString().split('T')[0] });
    setClienteSel(null); setDispStatus(null);
    setSelected(null); setModal('crear');
  };

  const openEditar = (c) => {
    setForm({ ...emptyForm, ...c, usarClienteRegistrado: !!c.clienteId });
    if (c.clienteId) setClienteSel(clientes.find(cl => cl.id === c.clienteId) || null);
    setDispStatus(null);
    setSelected(c); setModal('editar');
  };

  const openReagendar = (c) => {
    setForm({ ...emptyForm, ...c, fecha: '', hora: '', estado: 'programada', reagendadaDe: c.id });
    setDispStatus(null);
    setSelected(c); setModal('reagendar');
  };

  const closeModal = () => { setModal(null); setSelected(null); setDispStatus(null); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (dispStatus === 'ocupado') { toast.error('El usuario invitado tiene conflicto de horario'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.usarClienteRegistrado) payload.clienteId = null;
      delete payload.usarClienteRegistrado;

      if (modal === 'crear') {
        await api.post('/appointments', payload);
        toast.success('Cita agendada. Se enviarán recordatorios.');
      } else if (modal === 'reagendar') {
        // Mark original as cancelada BEFORE creating the new one, to avoid a false self-conflict
        await api.put(`/appointments/${selected.id}`, { ...selected, estado: 'cancelada' });
        const { id, createdAt, updatedAt, ...rest } = payload;
        await api.post('/appointments', { ...rest, reagendadaDe: selected.id });
        toast.success('Cita reagendada. Se notificó al invitado.');
      } else {
        await api.put(`/appointments/${selected.id}`, payload);
        toast.success('Cita actualizada');
      }
      await load(); closeModal();
    } catch(err) {
      toast.error(err.response?.data?.error || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async (c) => {
    if (!window.confirm('Eliminar esta cita?')) return;
    try { await api.delete(`/appointments/${c.id}`); toast.success('Eliminada'); await load(); }
    catch { toast.error('Error'); }
  };

  const openCompletar = (c) => {
    setModalCompletar(c);
    setPasoCompletar('preguntar');
    setNecesidadInput('');
    setClienteNuevoForm({
      empresa: c.empresaLugar || '', contactoNombre: c.contactoNombre || '',
      contactoEmail: c.contactoEmail || '', contactoTelefono: c.contactoTelefono || '',
    });
  };
  const closeCompletar = () => {
    setModalCompletar(null); setPasoCompletar('preguntar'); setNecesidadInput('');
  };

  const handleCompletarExitosa = async () => {
    if (!necesidadInput.trim()) { toast.error('Describe la necesidad detectada'); return; }
    setSavingCompletar(true);
    try {
      let clienteId = modalCompletar.clienteId;
      if (!clienteId) {
        const empresa = clienteNuevoForm.empresa.trim() || modalCompletar.empresaLugar;
        if (!empresa) { toast.error('El nombre de la empresa es requerido'); setSavingCompletar(false); return; }
        const { data: nuevoCliente } = await api.post('/clients', {
          empresa,
          contactoNombre: clienteNuevoForm.contactoNombre || null,
          contactoEmail: clienteNuevoForm.contactoEmail || null,
          contactoTelefono: clienteNuevoForm.contactoTelefono || null,
        });
        clienteId = nuevoCliente.id;
      }
      await api.put(`/appointments/${modalCompletar.id}`, { ...modalCompletar, estado: 'completada', necesidadDetectada: necesidadInput.trim(), clienteId });
      toast.success('Cita completada');
      closeCompletar();
      await load();
    } catch(e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setSavingCompletar(false); }
  };

  const irAReagendarDesdeFallo = () => {
    const c = modalCompletar;
    closeCompletar();
    openReagendar(c);
  };

  const handleDeclinar = async () => {
    setSavingCompletar(true);
    try {
      await api.put(`/appointments/${modalCompletar.id}`, { ...modalCompletar, estado: 'declinada' });
      if (modalCompletar.clienteId) {
        await api.put(`/clients/${modalCompletar.clienteId}`, { estado: 'inactivo' });
      }
      toast.success('Cita marcada como declinada');
      closeCompletar();
      await load();
    } catch(e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setSavingCompletar(false); }
  };

  const handleCrearProyecto = (c) => {
    navigate('/proyectos', { state: { prefillProyecto: {
      clientId: c.clienteId, descripcion: c.necesidadDetectada || '',
      atencionNombre: c.contactoNombre || '', citaId: c.id,
    } } });
  };

  const today = new Date().toISOString().split('T')[0];
  const filtered = citas.filter(c => {
    if (filtroUsuario && c.userId !== filtroUsuario) return false;
    if (filtro === 'proximas') return c.estado === 'programada' && c.fecha >= today;
    if (filtro === 'pasadas') return c.fecha < today || c.estado !== 'programada';
    return true;
  });

  const grouped = filtered.reduce((acc, c) => {
    const key = c.fecha?.slice(0, 7);
    if (!key) return acc;
    if (!acc[key]) acc[key] = [];
    acc[key].push(c);
    return acc;
  }, {});

  const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

  const formatDay = (dateStr) => {
    if (!dateStr) return { day: '--', month: '--', year: '--' };
    const [y, m, d] = dateStr.split('-');
    const months = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
    return { day: d, month: months[parseInt(m) - 1], year: y };
  };

  const invitadoDetalles = (c) => {
    const inv = usuarios.find(u => u.id === c.invitadoId);
    return inv ? `${inv.nombre} ${inv.apellido}` : null;
  };

  // ── FORM MODAL ───────────────────────────────────────────────────────────────
  const renderForm = () => (
    <div className="modal-overlay">
      <div className="modal modal-lg" style={{ maxWidth: 700 }}>
        <div className="modal-header">
          <h3 className="modal-title">
            {modal === 'crear' ? 'Agendar cita' : modal === 'reagendar' ? 'Reagendar cita' : 'Editar cita'}
          </h3>
          <button className="btn-icon" onClick={closeModal}><X size={18}/></button>
        </div>
        <form onSubmit={handleSave}>
          <div className="modal-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>

            {/* Recordatorio info */}
            {modal === 'crear' && (
              <div style={{ background:'#fffbeb', border:'1px solid #fef3c7', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:13, color:'#92400e' }}>
                Recibirás recordatorio 2 dias antes y 2 horas antes de la cita.
              </div>
            )}

            {/* ── EMPRESA / CLIENTE ── */}
            <div style={{ border:'1px solid #e5e5e5', borderRadius:10, padding:14, marginBottom:14 }}>
              <div style={{ fontSize:14, fontWeight:600, marginBottom:12, color:'#7B0D0D' }}>Empresa / Cliente</div>

              <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', marginBottom:12, fontSize:13 }}>
                <input type="checkbox" checked={form.usarClienteRegistrado}
                  onChange={fldCheck('usarClienteRegistrado')}
                  style={{ width:16, height:16, accentColor:'#7B0D0D' }}/>
                Cliente registrado en el sistema
              </label>

              {form.usarClienteRegistrado ? (
                <>
                  <div className="form-group">
                    <label className="form-label">Seleccionar cliente</label>
                    <select className="form-select" value={form.clienteId} onChange={e => handleClienteChange(e.target.value)}>
                      <option value="">Seleccionar empresa...</option>
                      {clientes.map(c => <option key={c.id} value={c.id}>{c.empresa}{c.contactoNombre?` — ${c.contactoNombre}`:''}</option>)}
                    </select>
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label className="form-label">Empresa / Lugar *</label>
                    <input className="form-input" required value={form.empresaLugar} onChange={fld('empresaLugar')} placeholder="Ej: Petroecuador — Refineria Esmeraldas"/>
                  </div>
                  <div className="form-row form-row-2">
                    <div className="form-group">
                      <label className="form-label">Nombre del contacto</label>
                      <input className="form-input" value={form.contactoNombre} onChange={fld('contactoNombre')} placeholder="Nombre"/>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Telefono</label>
                      <input className="form-input" value={form.contactoTelefono} onChange={fld('contactoTelefono')} placeholder="+593..."/>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Correo del contacto</label>
                    <input className="form-input" type="email" value={form.contactoEmail} onChange={fld('contactoEmail')} placeholder="correo@empresa.com"/>
                  </div>
                </>
              )}
              {/* Show contact info if cliente selected */}
              {form.usarClienteRegistrado && form.contactoNombre && (
                <div style={{ display:'flex', gap:12, padding:'8px 12px', background:'#f0f9ff', borderRadius:8, border:'1px solid #bae6fd', fontSize:12, flexWrap:'wrap' }}>
                  {form.contactoEmail && <span>Email: <strong>{form.contactoEmail}</strong></span>}
                  {form.contactoTelefono && <span>Tel: <strong>{form.contactoTelefono}</strong></span>}
                </div>
              )}
            </div>

            {/* ── UBICACION ── */}
            <div style={{ border:'1px solid #e5e5e5', borderRadius:10, padding:14, marginBottom:14 }}>
              <div style={{ fontSize:14, fontWeight:600, marginBottom:12, color:'#7B0D0D' }}>Ubicacion</div>
              <div className="form-group">
                <label className="form-label">Direccion</label>
                <input className="form-input" value={form.direccion} onChange={fld('direccion')} placeholder="Av. Principal 123, Guayaquil"/>
              </div>
              <div className="form-group">
                <label className="form-label">Buscar ubicacion en el mapa</label>
                <MapSelector
                  direccion={form.direccion}
                  linkMaps={form.linkMaps}
                  latitud={form.latitud}
                  longitud={form.longitud}
                  onSelect={(dir, link, lat, lng) => setForm(prev => ({
                    ...prev, direccion: dir, linkMaps: link, latitud: lat, longitud: lng
                  }))}
                />
              </div>
            </div>

            {/* ── FECHA / HORA ── */}
            <div className="form-row form-row-2">
              <div className="form-group">
                <label className="form-label">Fecha *</label>
                <input className="form-input" type="date" required value={form.fecha} onChange={fld('fecha')}
                  min={modal === 'editar' ? undefined : today}/>
              </div>
              <div className="form-group">
                <label className="form-label">Hora *</label>
                <input className="form-input" type="time" required value={form.hora} onChange={fld('hora')}/>
              </div>
            </div>

            {/* ── INVITAR USUARIO ── */}
            <div style={{ border:'1px solid #e5e5e5', borderRadius:10, padding:14, marginBottom:14 }}>
              <div style={{ fontSize:14, fontWeight:600, marginBottom:12, color:'#7B0D0D', display:'flex', alignItems:'center', gap:8 }}>
                <Users size={16}/> Invitar a otro usuario (opcional)
              </div>
              <div className="form-group" style={{ margin:0 }}>
                <select className="form-select" value={form.invitadoId} onChange={fld('invitadoId')}>
                  <option value="">Sin invitado</option>
                  {usuarios.map(u => <option key={u.id} value={u.id}>{u.nombre} {u.apellido} — {u.cargo||u.rol}</option>)}
                </select>
              </div>
              {form.invitadoId && (
                <div style={{ marginTop:10, display:'flex', alignItems:'center', gap:8, fontSize:13 }}>
                  {checkingDisp && <><RefreshCw size={14} style={{ animation:'spin 1s linear infinite' }}/> Verificando disponibilidad...</>}
                  {!checkingDisp && dispStatus === 'disponible' && (
                    <span style={{ color:'#16a34a', display:'flex', alignItems:'center', gap:6 }}>
                      <CheckCircle size={14}/> Disponible en ese horario
                    </span>)}
                  {!checkingDisp && dispStatus === 'ocupado' && (
                    <span style={{ color:'#dc2626', display:'flex', alignItems:'center', gap:6 }}>
                      <AlertTriangle size={14}/> Ya tiene una cita en ese horario
                    </span>)}
                  {!checkingDisp && !dispStatus && form.fecha && form.hora && (
                    <span style={{ color:'#888', fontSize:12 }}>Selecciona fecha y hora para verificar disponibilidad</span>)}
                </div>
              )}
            </div>

            {/* ── TEMA Y DOCUMENTOS ── */}
            <div className="form-group">
              <label className="form-label">Tema de la reunion *</label>
              <textarea className="form-textarea" required rows={2} value={form.tema} onChange={fld('tema')} placeholder="De que tratara la reunion?"/>
            </div>
            <div className="form-group">
              <label className="form-label">Documentos que debes llevar</label>
              <textarea className="form-textarea" rows={2} value={form.documentosNecesarios} onChange={fld('documentosNecesarios')} placeholder="Ej: Cotizacion, muestras de materiales..."/>
            </div>

            {/* ── ADJUNTO (QR, imagen, PDF) ── */}
            <div style={{ border:'1px solid #e5e5e5', borderRadius:10, padding:14, marginBottom:14 }}>
              <div style={{ fontSize:14, fontWeight:600, marginBottom:12, color:'#7B0D0D' }}>
                Adjunto (QR de acceso, imagen, PDF)
              </div>
              {form.adjuntoData ? (
                <div style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px', background:'#f0f9ff', borderRadius:8, border:'1px solid #bae6fd' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:600 }}>{form.adjuntoNombre}</div>
                    {form.adjuntoTipo?.startsWith('image/') && (
                      <img src={form.adjuntoData} alt="adjunto" style={{ maxHeight:120, maxWidth:'100%', borderRadius:6, marginTop:8, objectFit:'contain' }}/>
                    )}
                    {form.adjuntoTipo === 'application/pdf' && (
                      <div style={{ fontSize:12, color:'#888', marginTop:4 }}>PDF adjunto</div>
                    )}
                  </div>
                  <button type="button" onClick={() => setForm(p => ({ ...p, adjuntoData:'', adjuntoNombre:'', adjuntoTipo:'' }))}
                    style={{ background:'none', border:'none', cursor:'pointer', color:'#dc2626' }}><X size={16}/></button>
                </div>
              ) : (
                <div onClick={() => adjRef.current.click()}
                  style={{ border:'2px dashed #ccc', borderRadius:8, padding:20, textAlign:'center', cursor:'pointer', background:'#fafafa' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#7B0D0D'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = '#ccc'}>
                  <Upload size={24} style={{ color:'#ccc', marginBottom:6 }}/>
                  <p style={{ color:'#888', fontSize:13 }}>Clic para subir imagen, QR o PDF</p>
                  <p style={{ color:'#aaa', fontSize:11 }}>Max. 3MB</p>
                </div>
              )}
              <input ref={adjRef} type="file" accept="image/*,.pdf" style={{ display:'none' }} onChange={handleAdjunto}/>
            </div>

            {/* Estado (solo editar) */}
            {modal === 'editar' && (
              <div className="form-group">
                <label className="form-label">Estado</label>
                <select className="form-select" value={form.estado} onChange={fld('estado')}>
                  <option value="programada">Programada</option>
                  <option value="completada">Completada</option>
                  <option value="cancelada">Cancelada</option>
                </select>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Notas adicionales</label>
              <textarea className="form-textarea" rows={2} value={form.notas} onChange={fld('notas')} placeholder="Observaciones..."/>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={saving || dispStatus === 'ocupado'}>
              {saving ? <><span className="spinner"/> Guardando...</> :
               modal === 'crear' ? 'Agendar' :
               modal === 'reagendar' ? 'Reagendar' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  // ── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Citas y reuniones</h1>
          <p className="page-subtitle">{citas.filter(c => c.estado === 'programada' && c.fecha >= today).length} cita(s) proxima(s)</p>
        </div>
        <button className="btn btn-primary" onClick={openCrear}>
          <Plus size={16}/> Agendar cita
        </button>
      </div>

      {/* Filtros */}
      <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ display:'flex', gap:4, background:'white', padding:4, borderRadius:10, width:'fit-content', boxShadow:'var(--sombra)' }}>
          {[{ key:'proximas', label:'Proximas' }, { key:'todas', label:'Todas' }, { key:'pasadas', label:'Pasadas' }].map(t => (
            <button key={t.key} onClick={() => setFiltro(t.key)}
              style={{ padding:'7px 16px', borderRadius:8, border:'none', cursor:'pointer', fontSize:14, fontWeight:500, background:filtro===t.key?'#7B0D0D':'transparent', color:filtro===t.key?'white':'#666', transition:'all 0.2s' }}>
              {t.label}
            </button>
          ))}
        </div>
        {isGerencia && todosUsuarios.length > 0 && (
          <select className="form-select" style={{ width:200 }} value={filtroUsuario} onChange={e => setFiltroUsuario(e.target.value)}>
            <option value="">Todos los asesores</option>
            {todosUsuarios.map(u => <option key={u.id} value={u.id}>{u.nombre} {u.apellido}</option>)}
          </select>
        )}
        {filtroUsuario && <button className="btn btn-secondary btn-sm" onClick={() => setFiltroUsuario('')}>Limpiar</button>}
      </div>

      {/* Lista */}
      {loading ? (
        <div style={{ textAlign:'center', padding:40 }}><div className="spinner spinner-dark" style={{ width:30, height:30, margin:'0 auto' }}/></div>
      ) : filtered.length === 0 ? (
        <div className="card"><div className="empty-state"><CalendarDays size={48}/><h3>Sin citas</h3><p>Agenda una reunion para comenzar</p></div></div>
      ) : (
        Object.entries(grouped).sort((a, b) => b[0].localeCompare(a[0])).map(([month, items]) => {
          const [y, m] = month.split('-');
          return (
            <div key={month} style={{ marginBottom:28 }}>
              <h3 style={{ fontSize:15, fontWeight:700, color:'#7B0D0D', marginBottom:12, paddingBottom:6, borderBottom:'2px solid #fee2e2' }}>
                {MESES[parseInt(m) - 1]} {y}
              </h3>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {items.sort((a, b) => b.fecha?.localeCompare(a.fecha) || a.hora?.localeCompare(b.hora)).map(c => {
                  const d = formatDay(c.fecha);
                  const isPast = c.fecha < today;
                  const borderColor = c.estado === 'completada' ? '#16a34a' : c.estado === 'cancelada' ? '#dc2626' : isPast ? '#d97706' : '#7B0D0D';
                  const nombreInv = invitadoDetalles(c);
                  return (
                    <div key={c.id} style={{ background:'white', borderRadius:12, padding:'14px 18px', boxShadow:'var(--sombra)', display:'flex', gap:16, alignItems:'flex-start', borderLeft:`4px solid ${borderColor}`, opacity:isPast&&c.estado!=='completada'?0.75:1 }}>
                      {/* Fecha */}
                      <div style={{ textAlign:'center', minWidth:50, padding:'6px 10px', background:'#fff5f5', borderRadius:8, flexShrink:0 }}>
                        <div style={{ fontSize:22, fontWeight:800, color:'#7B0D0D', lineHeight:1 }}>{d.day}</div>
                        <div style={{ fontSize:11, color:'#888', fontWeight:600 }}>{d.month}</div>
                      </div>
                      {/* Info */}
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:4 }}>
                          <strong style={{ fontSize:15 }}>{c.empresaLugar}</strong>
                          {badgeEstado(c.estado)}
                          {c.reagendadaDe && <span className="badge badge-orange" style={{ fontSize:10 }}>Reagendada</span>}
                          {c.proyectoCreado && <span className="badge badge-green" style={{ fontSize:10 }}>→ Proyecto {c.proyectoCreado.referencia}</span>}
                        </div>
                        {c.necesidadDetectada && (
                          <div style={{ fontSize:12, color:'#555', marginBottom:3, padding:'4px 8px', background:'#f0fdf4', borderRadius:6, border:'1px solid #bbf7d0' }}>
                            Necesidad detectada: {c.necesidadDetectada}
                          </div>
                        )}
                        <div style={{ display:'flex', gap:16, fontSize:13, color:'#666', marginBottom:4, flexWrap:'wrap' }}>
                          <span style={{ display:'flex', alignItems:'center', gap:4 }}><Clock size={13}/>{c.hora?.slice(0,5)||''}</span>
                          <span>{c.tema}</span>
                        </div>
                        {c.contactoNombre && (
                          <div style={{ fontSize:12, color:'#555', marginBottom:3 }}>
                            Contacto: <strong>{c.contactoNombre}</strong>
                            {c.contactoTelefono && <span style={{ marginLeft:8, color:'#888' }}>{c.contactoTelefono}</span>}
                          </div>
                        )}
                        {(c.direccion || c.linkMaps) && (
                          <div style={{ fontSize:13, color:'#2563eb', marginBottom:4, display:'flex', alignItems:'center', gap:4 }}>
                            <MapPin size={13}/>
                            {c.direccion}
                            {c.linkMaps && (
                              <a href={c.linkMaps} target="_blank" rel="noopener noreferrer" style={{ marginLeft:8, color:'#7B0D0D', fontWeight:600 }}>
                                Ver en mapa
                              </a>
                            )}
                          </div>
                        )}
                        {nombreInv && (
                          <div style={{ fontSize:12, color:'#7B0D0D', display:'flex', alignItems:'center', gap:4, marginBottom:3 }}>
                            <Users size={12}/> Con: {nombreInv}
                          </div>
                        )}
                        {c.adjuntoData && c.adjuntoTipo?.startsWith('image/') && (
                          <div style={{ marginTop:6 }}>
                            <img src={c.adjuntoData} alt={c.adjuntoNombre} style={{ maxHeight:80, maxWidth:140, borderRadius:6, objectFit:'contain', border:'1px solid #e5e5e5', cursor:'pointer' }}
                              onClick={() => window.open(c.adjuntoData,'_blank')}
                              title={c.adjuntoNombre}/>
                          </div>
                        )}
                        {c.documentosNecesarios && (
                          <div style={{ fontSize:12, color:'#92400e', background:'#fffbeb', padding:'3px 8px', borderRadius:6, display:'inline-block', marginTop:3 }}>
                            Llevar: {c.documentosNecesarios}
                          </div>
                        )}
                      </div>
                      {/* Acciones */}
                      <div style={{ display:'flex', flexDirection:'column', gap:4, flexShrink:0 }}>
                        {c.estado === 'programada' && (
                          <>
                            <button className="btn-icon" title="Marcar completada" onClick={() => openCompletar(c)} style={{ color:'#16a34a' }}><CheckCircle size={16}/></button>
                            <button className="btn-icon" title="Reagendar" onClick={() => openReagendar(c)} style={{ color:'#d97706' }}><RefreshCw size={15}/></button>
                          </>
                        )}
                        {c.estado === 'completada' && c.clienteId && !c.proyectoCreado && (
                          <button className="btn-icon" title="Crear proyecto" onClick={() => handleCrearProyecto(c)} style={{ color:'#7B0D0D' }}><FolderPlus size={16}/></button>
                        )}
                        <button className="btn-icon" title="Editar" onClick={() => openEditar(c)}><Edit2 size={15}/></button>
                        <button className="btn-icon" title="Eliminar" onClick={() => handleDelete(c)} style={{ color:'#dc2626' }}><Trash2 size={15}/></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}

      {(modal === 'crear' || modal === 'editar' || modal === 'reagendar') && renderForm()}

      {modalCompletar && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth:480 }}>
            <div className="modal-header">
              <h3 className="modal-title">Completar cita</h3>
              <button className="btn-icon" onClick={closeCompletar}><X size={18}/></button>
            </div>
            <div className="modal-body">
              <div style={{ padding:'10px 14px', background:'#f0f9ff', borderRadius:8, border:'1px solid #bae6fd', marginBottom:14, fontSize:13 }}>
                {modalCompletar.empresaLugar}
              </div>

              {pasoCompletar === 'preguntar' && (
                <div>
                  <label className="form-label">¿La cita fue exitosa?</label>
                  <div style={{ display:'flex', gap:10, marginTop:8 }}>
                    <button type="button" className="btn btn-primary" style={{ flex:1 }} onClick={() => setPasoCompletar('exito')}>Sí, hubo éxito</button>
                    <button type="button" className="btn btn-secondary" style={{ flex:1 }} onClick={() => setPasoCompletar('fallo')}>No tuvimos éxito</button>
                  </div>
                </div>
              )}

              {pasoCompletar === 'fallo' && (
                <div>
                  <p style={{ fontSize:13, color:'#555', marginBottom:12 }}>¿Qué deseas hacer con este cliente?</p>
                  <div style={{ display:'flex', gap:10 }}>
                    <button type="button" className="btn btn-secondary" style={{ flex:1 }} onClick={irAReagendarDesdeFallo}>
                      <RefreshCw size={14} style={{ marginRight:4 }}/>Reagendar
                    </button>
                    <button type="button" className="btn" style={{ flex:1, background:'#dc2626', color:'white' }} onClick={handleDeclinar} disabled={savingCompletar}>
                      {savingCompletar ? <span className="spinner"/> : <><AlertTriangle size={14} style={{ marginRight:4 }}/>Declinar</>}
                    </button>
                  </div>
                </div>
              )}

              {pasoCompletar === 'exito' && (
                <>
                  <div className="form-group">
                    <label className="form-label">¿Qué necesidad le detectaste al cliente? *</label>
                    <textarea className="form-textarea" rows={4} value={necesidadInput} onChange={e => setNecesidadInput(e.target.value)}
                      placeholder="Ej: El techo de la bodega tiene filtraciones y necesita un sistema de revestimiento impermeable..."/>
                  </div>
                  {!modalCompletar.clienteId && (
                    <div style={{ border:'1px solid #e5e5e5', borderRadius:8, padding:12, marginTop:10 }}>
                      <div style={{ fontSize:12, color:'#7B0D0D', fontWeight:600, marginBottom:8 }}>Este cliente aún no está registrado — se creará al completar:</div>
                      <div className="form-group" style={{ marginBottom:8 }}>
                        <label className="form-label" style={{ fontSize:12 }}>Empresa *</label>
                        <input className="form-input" value={clienteNuevoForm.empresa} onChange={e => setClienteNuevoForm(p => ({ ...p, empresa:e.target.value }))}/>
                      </div>
                      <div className="form-group" style={{ marginBottom:8 }}>
                        <label className="form-label" style={{ fontSize:12 }}>Contacto</label>
                        <input className="form-input" value={clienteNuevoForm.contactoNombre} onChange={e => setClienteNuevoForm(p => ({ ...p, contactoNombre:e.target.value }))}/>
                      </div>
                      <div className="form-group" style={{ marginBottom:8 }}>
                        <label className="form-label" style={{ fontSize:12 }}>Email</label>
                        <input className="form-input" value={clienteNuevoForm.contactoEmail} onChange={e => setClienteNuevoForm(p => ({ ...p, contactoEmail:e.target.value }))}/>
                      </div>
                      <div className="form-group" style={{ marginBottom:0 }}>
                        <label className="form-label" style={{ fontSize:12 }}>Teléfono</label>
                        <input className="form-input" value={clienteNuevoForm.contactoTelefono} onChange={e => setClienteNuevoForm(p => ({ ...p, contactoTelefono:e.target.value }))}/>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              {pasoCompletar !== 'preguntar' && <button type="button" className="btn btn-secondary" onClick={() => setPasoCompletar('preguntar')}>Atrás</button>}
              <button type="button" className="btn btn-secondary" onClick={closeCompletar}>Cancelar</button>
              {pasoCompletar === 'exito' && (
                <button type="button" className="btn btn-primary" onClick={handleCompletarExitosa} disabled={savingCompletar}>
                  {savingCompletar ? <><span className="spinner"/> Guardando...</> : 'Guardar y completar'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
